import { PresignOperation, PresignResponse } from "./types";
import { presignApiRequestSchema, presignApiResponseSchema } from "./schemas";
import { ALLOWED_MIME_TYPES } from "@/constants";
import { parseResponseError } from "./utils/parseResponseError";

/**
 * Błąd odpowiedzi API presign/complete z kodem HTTP. Pozwala wywołującemu rozróżnić
 * 404/409/403 (np. limit skanów, umowa Polaka) od błędów sieci czy S3.
 */
export class FileStorageRequestError extends Error {
  constructor(
    message: string,
    /** Kod HTTP odpowiedzi API */
    public readonly status: number,
    /** Surowy komunikat z backendu (`message`/`error` z body), bez prefiksu kontekstu */
    public readonly apiMessage: string,
    /** Stabilny kod błędu z body (`code`), jeśli backend go podał (ASMMNT-83: błędy skanów) */
    public readonly apiCode?: string,
  ) {
    super(message);
    this.name = "FileStorageRequestError";
  }
}

/**
 * Odczytuje `code` z body błędu. Wołający przekazuje `response.clone()`, więc
 * `parseResponseError` dostaje potem nienaruszony strumień oryginału.
 */
async function readApiErrorCode(
  response: Response,
): Promise<string | undefined> {
  try {
    const body: unknown = await response.json();
    if (body && typeof body === "object" && "code" in body) {
      const code = (body as { code?: unknown }).code;
      return typeof code === "string" && code.length > 0 ? code : undefined;
    }
  } catch {
    // Body nie jest JSON-em (np. HTML z bramki): kod nieznany, klasyfikacja po statusie i treści.
  }
  return undefined;
}

export interface PresignRequest {
  presignUrl: string;
  attachmentId?: string;
  fieldName: string;
  filename: string;
  operation: PresignOperation;
}

export async function requestPresignedUrl(
  params: PresignRequest,
): Promise<PresignResponse> {
  const { presignUrl, attachmentId, fieldName, filename, operation } = params;
  const requestBody = presignApiRequestSchema.parse({
    attachment_id: attachmentId,
    filename,
    field_name: fieldName,
    operation,
  });

  const response = await fetch(presignUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const apiCode = await readApiErrorCode(response.clone());
    const errorMessage = await parseResponseError(response);
    throw new FileStorageRequestError(
      `Failed to request presigned URL: ${errorMessage}`,
      response.status,
      errorMessage,
      apiCode,
    );
  }

  const body = await response.json();
  const parsed = presignApiResponseSchema.parse(body);
  const payload = parsed.data;

  return {
    attachmentId: payload.attachment_id,
    filename: payload.filename,
    fieldName: payload.field_name,
    s3Key: payload.s3_key,
    presignedUrl: payload.presigned_url,
    operation: payload.operation,
    contentType: payload.content_type,
    createOnly: payload.create_only,
    metadata: payload.metadata,
  };
}

/**
 * Potwierdza operację na załączniku (`complete` / `complete-delete`) i zwraca body odpowiedzi.
 * Kształt body zależy od kontekstu (ankieta: ignorowane; skany umowy: skan + pola potwierdzeń),
 * więc wywołujący parsuje je własnym schematem.
 */
export async function completeAttachment({
  completionUrl,
  attachmentId,
}: {
  completionUrl: string;
  attachmentId: string;
}): Promise<unknown> {
  const response = await fetch(completionUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ attachment_id: attachmentId }),
  });

  if (!response.ok) {
    const apiCode = await readApiErrorCode(response.clone());
    const errorMessage = await parseResponseError(response);
    throw new FileStorageRequestError(
      `Failed to complete attachment operation: ${errorMessage}`,
      response.status,
      errorMessage,
      apiCode,
    );
  }

  // Backend odpowiada JSON-em `{ ok, data }`; 200 bez body (np. z proxy) nie może udawać nieudanego
  // uploadu — wołający, którym zależy na body (skany), traktują `null` jak zły kształt.
  return response.json().catch(() => null);
}

const buildUploadHeaders = (
  contentType?: string,
  fileType?: string,
  metadata?: { confirmed_at?: string | null },
): Record<string, string> => {
  const headers: Record<string, string> = {};
  const finalContentType =
    contentType || fileType || "application/octet-stream";

  if (ALLOWED_MIME_TYPES.includes(finalContentType)) {
    headers["Content-Type"] = finalContentType;
  }

  if (metadata?.confirmed_at) {
    headers["x-amz-meta-confirmed_at"] = metadata.confirmed_at;
  }

  return headers;
};

export async function uploadWithPresignedUrl(
  presignedUrl: string,
  file: File,
  contentType?: string,
  metadata?: { confirmed_at?: string | null },
  createOnly = false,
): Promise<void> {
  const headers = buildUploadHeaders(contentType, file.type, metadata);
  if (createOnly) {
    headers["If-None-Match"] = "*";
  }

  const response = await fetch(presignedUrl, {
    method: "PUT",
    body: file,
    headers,
  });

  if (!response.ok) {
    const errorMessage = await parseResponseError(response);
    throw new Error(`Failed to upload file: ${errorMessage}`);
  }
}

/**
 * Deletes a file from S3 using a presigned URL.
 */
export async function deleteWithPresignedUrl(
  presignedUrl: string,
): Promise<void> {
  const response = await fetch(presignedUrl, {
    method: "DELETE",
  });

  if (!response.ok) {
    const errorMessage = await parseResponseError(response);
    throw new Error(`Failed to delete file: ${errorMessage}`);
  }
}
